import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

// =============================================================================
// Types
// =============================================================================

export type SubscriptionPlan = 'solo' | 'pro' | 'team';

export type SubscriptionStatus =
  | 'pending'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'internal';

export type BillingPeriod = 'monthly' | 'yearly';

// Plan from the plans table (matches DB column names)
export interface Plan {
  id: string;
  plan_name: string;
  plan_type: SubscriptionPlan;
  billing_period: BillingPeriod;
  price: number;
  tier_rank: number;
  max_businesses: number;
  features: Record<string, unknown>;
  is_active: boolean;
}

// Payment method from the payment_methods table
export interface PaymentMethod {
  id: string;
  stripe_payment_method_id: string;
  card_brand: string;
  card_last4: string;
  card_exp_month: number;
  card_exp_year: number;
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
}

// Proration preview for plan changes
export interface ProrationPreview {
  amount_due: number;
  currency: string;
  proration_date: number;
  lines: Array<{
    description: string;
    amount: number;
  }>;
}

// Plan change preview (includes upgrade/downgrade detection)
export interface PlanChangePreview {
  changeType: 'upgrade' | 'downgrade' | 'same';
  effectiveImmediately: boolean;
  proration?: ProrationPreview;
  scheduledDate?: string; // For downgrades - when it takes effect
}

// Subscription with new fields
export interface Subscription {
  id: string;
  user_id: string;
  plan_type: SubscriptionPlan;
  status: SubscriptionStatus;
  max_businesses: number;
  features: Record<string, unknown>;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
  // New fields from migration
  current_plan_id: string | null;
  scheduled_plan_id: string | null;
  scheduled_plan_change_date: string | null;
  cancellation_scheduled_date: string | null;
  billing_cycle_start: string | null;
  billing_cycle_end: string | null;
  // Joined plan data
  current_plan?: Plan;
  scheduled_plan?: Plan;
}

// =============================================================================
// Helper Functions
// =============================================================================

export function isSubscriptionActive(status: SubscriptionStatus): boolean {
  return ['active', 'trialing', 'past_due', 'internal'].includes(status);
}

export function requiresPayment(status: SubscriptionStatus): boolean {
  return status === 'pending';
}

export function isCanceled(status: SubscriptionStatus): boolean {
  return status === 'canceled';
}

/**
 * Check if subscription is scheduled to cancel at period end.
 * User still has active access until the period end date.
 */
export function isScheduledForCancellation(subscription: Subscription | null): boolean {
  if (!subscription) return false;
  return subscription.cancel_at_period_end && isSubscriptionActive(subscription.status);
}

/**
 * Check if subscription has a scheduled downgrade.
 */
export function hasScheduledDowngrade(subscription: Subscription | null): boolean {
  if (!subscription) return false;
  return !!subscription.scheduled_plan_id && !!subscription.scheduled_plan_change_date;
}

/**
 * Format card brand for display.
 */
export function formatCardBrand(brand: string): string {
  const brands: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return brands[brand.toLowerCase()] || brand;
}

/**
 * Format price (in dollars) to display string.
 */
export function formatPrice(dollars: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(dollars);
}

/**
 * Format Stripe amount (in cents) to display string.
 */
export function formatStripeCents(cents: number, currency = 'usd'): string {
  return formatPrice(cents / 100, currency);
}

// =============================================================================
// useSubscription Hook
// =============================================================================

export function useSubscription() {
  const queryClient = useQueryClient();
  const { user, session, initialized } = useAuth();

  // Fetch current subscription with plan details
  const {
    data: subscription,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async (): Promise<Subscription | null> => {
      if (!user?.id || !session?.access_token) {
        return null;
      }

      const response = await api.get<{ subscription: Subscription | null }>(
        '/subscription',
        session.access_token
      );

      if (!response.success) {
        if (response.code === 'NOT_FOUND') {
          return null;
        }
        throw new Error(response.error);
      }

      return response.data?.subscription ?? null;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    enabled: initialized && !!user && !!session?.access_token,
  });

  // Create setup intent for Stripe Elements
  const createSetupIntentMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{ clientSecret: string; customerId: string }>(
        '/subscription/setup-intent',
        {},
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
  });

  // Create subscription with a payment method
  const createSubscriptionMutation = useMutation({
    mutationFn: async ({
      planId,
      paymentMethodId,
    }: {
      planId: string;
      paymentMethodId: string;
    }) => {
      const response = await api.post<{ subscription: Subscription }>(
        '/subscription/create',
        { planId, paymentMethodId },
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  // Preview plan change (proration)
  const previewPlanChangeMutation = useMutation({
    mutationFn: async ({ newPlanId }: { newPlanId: string }) => {
      const response = await api.post<PlanChangePreview>(
        '/subscription/preview-plan-change',
        { newPlanId },
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
  });

  // Execute plan change
  const changePlanMutation = useMutation({
    mutationFn: async ({ newPlanId }: { newPlanId: string }) => {
      const response = await api.post<{
        changeType: 'upgrade' | 'downgrade';
        effectiveDate: string;
        newPlan: string;
      }>(
        '/subscription/change-plan',
        { newPlanId },
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  // Cancel scheduled downgrade
  const cancelDowngradeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{ subscription: Subscription }>(
        '/subscription/cancel-downgrade',
        {},
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  // Schedule cancellation at period end
  const scheduleCancellationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{ subscription: Subscription; cancellation_date: string }>(
        '/subscription/schedule-cancellation',
        {},
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  // Cancel scheduled cancellation
  const cancelScheduledCancellationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{ subscription: Subscription }>(
        '/subscription/cancel-scheduled-cancellation',
        {},
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  // Invalidate and refetch subscription
  const invalidate = () => {
    queryClient.removeQueries({ queryKey: ['subscription'], exact: false });
  };

  return {
    // Subscription data
    subscription,
    isLoading,
    error,
    refetch,
    invalidate,

    // Setup intent for payment collection
    createSetupIntent: createSetupIntentMutation.mutateAsync,
    isSetupIntentLoading: createSetupIntentMutation.isPending,
    setupIntentError: createSetupIntentMutation.error,

    // Create subscription
    createSubscription: createSubscriptionMutation.mutateAsync,
    isCreateLoading: createSubscriptionMutation.isPending,
    createError: createSubscriptionMutation.error,

    // Plan change preview
    previewPlanChange: previewPlanChangeMutation.mutateAsync,
    isPreviewLoading: previewPlanChangeMutation.isPending,
    previewError: previewPlanChangeMutation.error,

    // Change plan
    changePlan: changePlanMutation.mutateAsync,
    isChangePlanLoading: changePlanMutation.isPending,
    changePlanError: changePlanMutation.error,

    // Cancel downgrade
    cancelDowngrade: cancelDowngradeMutation.mutateAsync,
    isCancelDowngradeLoading: cancelDowngradeMutation.isPending,
    cancelDowngradeError: cancelDowngradeMutation.error,

    // Schedule cancellation
    scheduleCancellation: scheduleCancellationMutation.mutateAsync,
    isScheduleCancellationLoading: scheduleCancellationMutation.isPending,
    scheduleCancellationError: scheduleCancellationMutation.error,

    // Cancel scheduled cancellation
    cancelScheduledCancellation: cancelScheduledCancellationMutation.mutateAsync,
    isCancelScheduledCancellationLoading: cancelScheduledCancellationMutation.isPending,
    cancelScheduledCancellationError: cancelScheduledCancellationMutation.error,
  };
}

// =============================================================================
// usePlans Hook
// =============================================================================

export function usePlans() {
  const { session, initialized } = useAuth();

  const {
    data: plans,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: async (): Promise<Plan[]> => {
      const response = await api.get<{ plans: Plan[] }>(
        '/subscription/plans',
        session?.access_token
      );

      if (!response.success) {
        throw new Error(response.error);
      }

      return response.data?.plans ?? [];
    },
    staleTime: 1000 * 60 * 30, // 30 minutes - plans don't change often
    retry: 1,
    enabled: initialized,
  });

  // Group plans by type for easy access
  const plansByType = plans?.reduce<Partial<Record<SubscriptionPlan, Partial<Record<BillingPeriod, Plan>>>>>((acc, plan) => {
    if (!acc[plan.plan_type]) {
      acc[plan.plan_type] = {};
    }
    acc[plan.plan_type]![plan.billing_period] = plan;
    return acc;
  }, {});

  return {
    plans,
    plansByType,
    isLoading,
    error,
    getPlan: (type: SubscriptionPlan, period: BillingPeriod) =>
      plansByType?.[type]?.[period],
  };
}

// =============================================================================
// usePaymentMethods Hook
// =============================================================================

export function usePaymentMethods() {
  const queryClient = useQueryClient();
  const { user, session, initialized } = useAuth();

  // Fetch payment methods
  const {
    data: paymentMethods,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['paymentMethods', user?.id],
    queryFn: async (): Promise<PaymentMethod[]> => {
      if (!user?.id || !session?.access_token) {
        return [];
      }

      const response = await api.get<{ paymentMethods: PaymentMethod[] }>(
        '/subscription/payment-methods',
        session.access_token
      );

      if (!response.success) {
        throw new Error(response.error);
      }

      return response.data?.paymentMethods ?? [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    enabled: initialized && !!user && !!session?.access_token,
  });

  // Save a new payment method
  const savePaymentMethodMutation = useMutation({
    mutationFn: async ({
      paymentMethodId,
      setAsDefault = true,
    }: {
      paymentMethodId: string;
      setAsDefault?: boolean;
    }) => {
      const response = await api.post<{ paymentMethod: PaymentMethod }>(
        '/subscription/payment-methods',
        { paymentMethodId, setAsDefault },
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
  });

  // Remove a payment method
  const removePaymentMethodMutation = useMutation({
    mutationFn: async ({ paymentMethodId }: { paymentMethodId: string }) => {
      const response = await api.delete<{ success: boolean }>(
        `/subscription/payment-methods/${paymentMethodId}`,
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
  });

  // Set default payment method
  const setDefaultPaymentMethodMutation = useMutation({
    mutationFn: async ({ paymentMethodId }: { paymentMethodId: string }) => {
      const response = await api.post<{ paymentMethod: PaymentMethod }>(
        `/subscription/payment-methods/${paymentMethodId}/default`,
        {},
        session?.access_token
      );
      if (!response.success) {
        throw new Error(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
  });

  // Get the default payment method
  const defaultPaymentMethod = paymentMethods?.find(pm => pm.is_primary);

  return {
    paymentMethods,
    defaultPaymentMethod,
    isLoading,
    error,
    refetch,

    // Save payment method
    savePaymentMethod: savePaymentMethodMutation.mutateAsync,
    isSaveLoading: savePaymentMethodMutation.isPending,
    saveError: savePaymentMethodMutation.error,

    // Remove payment method
    removePaymentMethod: removePaymentMethodMutation.mutateAsync,
    isRemoveLoading: removePaymentMethodMutation.isPending,
    removeError: removePaymentMethodMutation.error,

    // Set default
    setDefaultPaymentMethod: setDefaultPaymentMethodMutation.mutateAsync,
    isSetDefaultLoading: setDefaultPaymentMethodMutation.isPending,
    setDefaultError: setDefaultPaymentMethodMutation.error,
  };
}

// =============================================================================
// useStoredPlan Hook
// =============================================================================

/**
 * Hook to get stored plan from sessionStorage (from ?plan= param)
 */
export function useStoredPlan() {
  const PLAN_STORAGE_KEY = 'sapling-selected-plan';
  const PERIOD_STORAGE_KEY = 'sapling-selected-period';

  const getStoredPlan = (): SubscriptionPlan | null => {
    const stored = sessionStorage.getItem(PLAN_STORAGE_KEY);
    if (stored && ['solo', 'pro', 'team'].includes(stored)) {
      return stored as SubscriptionPlan;
    }
    return null;
  };

  const getStoredPeriod = (): BillingPeriod => {
    const stored = sessionStorage.getItem(PERIOD_STORAGE_KEY);
    if (stored === 'yearly') {
      return 'yearly';
    }
    return 'monthly';
  };

  const setStoredPlan = (plan: SubscriptionPlan, period: BillingPeriod = 'monthly') => {
    sessionStorage.setItem(PLAN_STORAGE_KEY, plan);
    sessionStorage.setItem(PERIOD_STORAGE_KEY, period);
  };

  const clearStoredPlan = () => {
    sessionStorage.removeItem(PLAN_STORAGE_KEY);
    sessionStorage.removeItem(PERIOD_STORAGE_KEY);
  };

  return {
    storedPlan: getStoredPlan(),
    storedPeriod: getStoredPeriod(),
    getStoredPlan,
    getStoredPeriod,
    setStoredPlan,
    clearStoredPlan,
  };
}
